import {
  Button,
  Flex,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { isNotEmpty, useForm } from "@mantine/form";
import get from "lodash/get";
import { useCallback, useMemo } from "react";

import SvgCalendar from "../../../components/Icons/Calendar.tsx";
import {
  CaResult,
  LimitRequestException,
  LimitRequestType,
  UpdateCreditApprovalInput,
  UserRole,
} from "../../../graphql/generated.ts";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import {
  CreditApproval,
  creditApprovalCCResultOptions,
  creditApprovalResultOptions,
  creditApprovalRRResultOptions,
} from "../../../types/creditApproval.ts";
import {
  LimitRequestDetail,
  pricingApprovedMapping,
  tenorOptions,
} from "../../../types/limitRequest/limitRequest.ts";
import { LimitRequestConditionPrecedent } from "../../../types/limitRequestConditionPrecedent.ts";
import { parseStringToNumber, priceFormatter } from "../../../utils/number.ts";
import ConditionPrecedents from "../ConditionPrecedents/ConditionPrecedents.tsx";
import Exceptions from "../Exceptions/Exceptions.tsx";
import { useStyles } from "./CreditCommitteeModal.styles.ts";

interface Props {
  value: Partial<CreditApproval> | null | undefined;
  limitRequest: LimitRequestDetail | null | undefined;
  exceptions: LimitRequestException[] | null | undefined;
  conditionPrecedents: LimitRequestConditionPrecedent[] | null | undefined;
  opened: boolean;
  onClose: () => void;
  onSubmit: (value: UpdateCreditApprovalInput) => void;
  onRefreshConditionPrecedence: () => void;
  onRefreshExceptionsList: () => void;
}

const CreditCommitteeModal = ({
  value,
  limitRequest,
  exceptions,
  conditionPrecedents,
  opened,
  onClose,
  onSubmit,
  onRefreshConditionPrecedence,
  onRefreshExceptionsList,
}: Props) => {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();

  const isFieldRequired = (
    key: keyof CreditApproval,
    values: Record<string, unknown>
  ): boolean => {
    if (key === "result") {
      return true;
    }
    const result = values.result as CaResult | undefined;
    return !(
      !!result &&
      [CaResult.RejectedByCc, CaResult.RejectedByRr].includes(result)
    );
  };

  const form = useForm({
    initialValues: {
      limitName: value?.limitName ?? limitRequest?.limitName,
      date: value?.date ? new Date(value?.date) : undefined,
      result: value?.result,
      limitRecommendation:
        value?.limitRecommendation && Number(value.limitRecommendation),
      tenorRecommendation: value?.tenorRecommendation
        ? String(value.tenorRecommendation)
        : undefined,
      pricingRecommendation:
        value?.pricingRecommendation && Number(value.pricingRecommendation),
      insuranceLimitRecommendation:
        value?.insuranceLimitRecommendation &&
        Number(value.insuranceLimitRecommendation),
      notes: value?.notes,
    },
    validate: {
      date: (value: Date | undefined, values: Record<string, unknown>) =>
        isFieldRequired("date", values) &&
        isNotEmpty("Date is required")(value),
      result: isNotEmpty("Result is required"),
      limitRecommendation: (
        value: string | undefined,
        values: Record<string, unknown>
      ) =>
        isFieldRequired("limitRecommendation", values) &&
        isNotEmpty("Limit Approved is required")(value),
      tenorRecommendation: (
        value: string | undefined,
        values: Record<string, unknown>
      ) =>
        isFieldRequired("tenorRecommendation", values) &&
        isNotEmpty("Tenor Approved is required")(value),
      pricingRecommendation: (
        value: string | undefined,
        values: Record<string, unknown>
      ) =>
        isFieldRequired("pricingRecommendation", values) &&
        isNotEmpty("Pricing Approved is required")(value),
      insuranceLimitRecommendation: (
        value: string | undefined,
        values: Record<string, unknown>
      ) =>
        isFieldRequired("insuranceLimitRecommendation", values) &&
        isNotEmpty("Insurance Limit Approved is required")(value),
    },
    validateInputOnBlur: true,
  });

  const resultOptions = useMemo(() => {
    if (!user) {
      return [];
    }
    if ([UserRole.SuperAdmin, UserRole.Admin].includes(user.role)) {
      return creditApprovalResultOptions;
    }
    if (
      [
        UserRole.RrManager,
        UserRole.RrOfficer,
        UserRole.RmonOfficer,
        UserRole.RmonManager,
      ].includes(user.role)
    ) {
      return creditApprovalRRResultOptions;
    }
    if (user.role === UserRole.CreditCommittee) {
      return creditApprovalCCResultOptions;
    }
    return [];
  }, [user]);

  const handleSelectTenor = (value: string | null) => {
    form.setValues((prevValues) => {
      return {
        ...prevValues,
        tenorRecommendation: value ?? undefined,
        pricingRecommendation: value
          ? get(pricingApprovedMapping, Number(value))
          : undefined,
      };
    });
  };

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }
    onSubmit({
      ...form.values,
      tenorRecommendation: Number(form.values.tenorRecommendation),
    } as UpdateCreditApprovalInput);
    handleClose();
  }, [form, handleClose, onSubmit]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeButtonProps={{
        size: 24,
        iconSize: 24,
      }}
      title={value ? "Edit Credit Committee" : "Add Credit Committee"}
      scrollAreaComponent={ScrollArea.Autosize}
      size="auto"
    >
      <Flex direction="column" gap={20} className={classes.contentWrapper}>
        <Flex gap={20}>
          <Flex direction="column" w="50%" gap={12}>
            <TextInput
              size="m"
              label="Limit Name"
              placeholder="Enter Limit Name"
              disabled={
                limitRequest?.limitRequestType !==
                LimitRequestType.NewProjectLimit
              }
              {...form.getInputProps("limitName")}
            />
            <DatePickerInput
              required={isFieldRequired("date", form.values)}
              label="CC Date"
              placeholder="Select CC date"
              icon={<SvgCalendar />}
              firstDayOfWeek={0}
              size="m"
              valueFormat="YYYY MMM DD"
              popoverProps={{ withinPortal: true, zIndex: 1000 }}
              clearable
              {...form.getInputProps("date")}
            />
            <NumberInput
              required={isFieldRequired("limitRecommendation", form.values)}
              label="Limit Approved"
              placeholder="Enter Limit Approved"
              size="m"
              precision={2}
              parser={parseStringToNumber}
              formatter={priceFormatter}
              min={0}
              hideControls
              {...form.getInputProps("limitRecommendation")}
            />
            <Select
              required={isFieldRequired("tenorRecommendation", form.values)}
              size="m"
              label="Tenor Approved"
              placeholder="Tenor Approved"
              data={tenorOptions}
              {...form.getInputProps("tenorRecommendation")}
              onChange={(value) => handleSelectTenor(value)}
            />
            <NumberInput
              required={isFieldRequired("pricingRecommendation", form.values)}
              label="Pricing Approved"
              placeholder="Enter Pricing Approved"
              size="m"
              precision={2}
              parser={parseStringToNumber}
              formatter={priceFormatter}
              min={0}
              hideControls
              {...form.getInputProps("pricingRecommendation")}
            />
            <NumberInput
              required={isFieldRequired(
                "insuranceLimitRecommendation",
                form.values
              )}
              label="Insurance Limit Approved"
              placeholder="Enter Insurance Limit Approved"
              size="m"
              precision={2}
              parser={parseStringToNumber}
              formatter={priceFormatter}
              min={0}
              hideControls
              {...form.getInputProps("insuranceLimitRecommendation")}
            />
            <Select
              required={isFieldRequired("result", form.values)}
              withinPortal
              dropdownPosition="top"
              data={resultOptions}
              label="Result"
              placeholder="Select Result"
              size="m"
              {...form.getInputProps("result")}
            />
            <Textarea
              label="Notes"
              placeholder="Enter Notes"
              autosize
              size="m"
              minRows={3}
              maxRows={3}
              {...form.getInputProps("notes")}
            />
          </Flex>
          <Flex direction="column" w="50%" gap={12}>
            {limitRequest?.id && (
              <ConditionPrecedents
                limitRequestId={limitRequest?.id}
                conditionPrecedents={conditionPrecedents}
                onRefreshConditionPrecedents={onRefreshConditionPrecedence}
              />
            )}
            {limitRequest?.id && (
              <Exceptions
                limitRequestId={limitRequest?.id}
                exceptions={exceptions}
                onRefreshExceptions={onRefreshExceptionsList}
              />
            )}
          </Flex>
        </Flex>
        <Flex gap={20} justify="flex-end">
          <Button variant="outlineBlue" uppercase onClick={handleClose}>
            cancel
          </Button>
          <Button
            variant="filledBlue"
            uppercase
            disabled={!form.isValid()}
            onClick={handleSubmit}
          >
            save
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default CreditCommitteeModal;
